import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DashboardLayout } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import TableWithSearch from "@/components/TableWithSearch";
import { userManagementTableHeaders } from "@/utils/tablesData";
import { KeyRound, Lock, Plus } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { toast } from "sonner";
import permissionDefaults from "@/utils/Permission.json";
import AxiosInstance from "@/utils/AxiosInstance";
import { getPromise, getPromiseToast } from "@/utils/helpers";
import { capitalizeTheStartingCharacterFilter } from "@/constants/payloadFilters";
import RHFWrapper from "@/components/RHFWrapper";
import RhfInput from "@/components/rhfFields/RhfInput";
import { ResetSchema } from "@/pages/ResetPassword";

const PROTECTED_ADMIN_EMAIL = "admin@anthro.care";

function isAdminPosition(position: string) {
  return String(position).trim().toLowerCase() === "admin";
}

function isManagerPosition(position: string) {
  return String(position).trim().toLowerCase() === "manager";
}

const POSITION_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "manager", label: "Manager" },
  // { value: "staff", label: "Staff" },
  // { value: "coordinator", label: "Coordinator" },
] as const;

export type UserAccountStatus = "active" | "suspended" | "inactive";

const USER_ACCOUNT_STATUS_OPTIONS: { value: UserAccountStatus; label: string }[] =
  [
    { value: "active", label: "Active" },
    { value: "suspended", label: "Suspended" },
  ];

/** Keys/labels aligned with each user row `permissions` from `GET /users` (navigation access only). */
export const ACCESS_NAV_PERMISSION_ITEMS = [
  { key: "canAccessOrganizations", label: "Organizations" },
  { key: "canAccessClinicians", label: "Clinicians" },
  { key: "canAccessCaretakers", label: "Caretakers" },
  { key: "canAccessPatients", label: "Patients" },
  { key: "canAccessEngage", label: "Engage" },
  { key: "canAccessTriage", label: "Triage" },
  { key: "canAccessReports", label: "Reports" },
  { key: "canAccessPrograms", label: "Programs" },
  { key: "canAccessSupport", label: "Support" },
  { key: "canAccessConfig", label: "Config" },
  { key: "canAccessPastMedicalRecords", label: "Past Medical Records" },
  { key: "canAccessAIChat", label: "Anthro AI" },
] as const;

export type PermissionCatalogItem = { key: string; label: string };

const FALLBACK_NAV_KEYS = ACCESS_NAV_PERMISSION_ITEMS.map((x) => x.key);
const FALLBACK_KEY_SET = new Set<string>(FALLBACK_NAV_KEYS);

export type OrganizationAssignmentScope = "all" | "selected";

export type OrganizationAssignment =
  | { scope: "all" }
  | { scope: "selected"; organizationIds: string[] };

const ALL_ORGANIZATIONS_ASSIGNMENT: OrganizationAssignment = { scope: "all" };

type OrgListItem = { _id: string; organizationName: string };

export type ManagedUser = {
  id: string;
  name: string;
  email: string;
  position: string;
  status: UserAccountStatus;
  enabled: boolean;
  permissions: Record<string, boolean>;
  organizationAssignment: OrganizationAssignment;
};

function rawAdminPermissionKeys(): string[] {
  return Object.keys(permissionDefaults.admin || {});
}

function emptyPermissions(): Record<string, boolean> {
  return Object.fromEntries(rawAdminPermissionKeys().map((k) => [k, false]));
}

function normalizePermissions(p: Record<string, boolean>): Record<string, boolean> {
  const base = emptyPermissions();
  for (const k of rawAdminPermissionKeys()) {
    if (k in p) base[k] = !!p[k];
  }
  const nursing = !!(base.canAddNursingNotes || base.CanAddNursingNotes);
  base.canAddNursingNotes = nursing;
  base.CanAddNursingNotes = nursing;
  return base;
}

function emptyAccessPermissionDraft(keys: string[]): Record<string, boolean> {
  return Object.fromEntries(keys.map((k) => [k, false]));
}

function accessPermissionsFromSource(
  source: Record<string, unknown>,
  keys: string[],
): Record<string, boolean> {
  const out: Record<string, boolean> = {};
  for (const key of keys) {
    if (source[key] !== undefined) out[key] = !!source[key];
    else out[key] = false;
  }
  return out;
}

/** Preserve admin template flags + any `canAccess*` / known nav keys from API user.permissions */
function mergeStoredPermissions(
  apiPermissions: Record<string, unknown>,
): Record<string, boolean> {
  const adminNorm = normalizePermissions(apiPermissions as Record<string, boolean>);
  const dynamicAccess: Record<string, boolean> = {};
  for (const [k, v] of Object.entries(apiPermissions)) {
    const include =
      k.startsWith("canAccess") || FALLBACK_KEY_SET.has(k);
    if (!include) continue;
    if (typeof v === "boolean") dynamicAccess[k] = v;
    else if (v === "true" || v === "false") dynamicAccess[k] = v === "true";
    else if (typeof v === "number") dynamicAccess[k] = v !== 0;
  }
  return { ...adminNorm, ...dynamicAccess };
}

/** Body for PUT `/users/:id/permissions` — one boolean per `ACCESS_NAV_PERMISSION_ITEMS` key. */
function buildAccessPermissionsPayload(
  draft: Record<string, boolean>,
  keys: string[],
): { permissions: Record<string, boolean> } {
  const permissions: Record<string, boolean> = {};
  for (const key of keys) {
    permissions[key] = !!draft[key];
  }
  return { permissions };
}

function buildAccessDraftFromUser(
  u: ManagedUser,
  keys: string[],
): Record<string, boolean> {
  return accessPermissionsFromSource(u.permissions as Record<string, unknown>, keys);
}

/** Merge static access keys list with latest values from stored user.permissions */
function mergeCatalogKeysIntoDraft(
  prev: Record<string, boolean>,
  user: ManagedUser,
  keys: string[],
): Record<string, boolean> {
  const next: Record<string, boolean> = {};
  for (const k of keys) {
    next[k] =
      prev[k] !== undefined ? !!prev[k] : !!user.permissions[k];
  }
  return next;
}

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

function AccessNavPermissionFields({
  items,
  draft,
  onToggle,
  idPrefix,
}: {
  items: PermissionCatalogItem[];
  draft: Record<string, boolean>;
  onToggle: (key: string, checked: boolean) => void;
  idPrefix: string;
}) {
  return (
    <div
      className="rounded-lg border border-border bg-muted/25 p-1 max-h-[min(280px,42vh)] overflow-y-auto"
      role="group"
      aria-label="Navigation access"
    >
      {items.map(({ key, label }) => (
        <div
          key={key}
          className="flex items-start gap-3 rounded-md px-3 py-2.5 hover:bg-muted/70 transition-colors"
        >
          <Checkbox
            id={`${idPrefix}-${key}`}
            checked={!!draft[key]}
            onCheckedChange={(v) => onToggle(key, v === true)}
            className="mt-0.5"
          />
          <label
            htmlFor={`${idPrefix}-${key}`}
            className="text-sm leading-snug cursor-pointer text-foreground"
          >
            {label}
          </label>
        </div>
      ))}
    </div>
  );
}
function extractUsersList(body: unknown): unknown[] {
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data ?? root;
  if (Array.isArray(data)) return data;
  if (typeof data === "object" && data !== null) {
    const block = data as Record<string, unknown>;
    if (Array.isArray(block.users)) return block.users;
    if (Array.isArray(block.data)) return block.data;
  }
  return [];
}

function normalizeAccountStatus(raw: unknown): UserAccountStatus {
  const s = String(raw ?? "").trim().toLowerCase();
  if (s === "active") return "active";
  if (s === "inactive") return "inactive";
  return "suspended";
}

function parseOrganizationAssignment(raw: unknown): OrganizationAssignment {
  if (!raw || typeof raw !== "object") return { scope: "all" };
  const o = raw as Record<string, unknown>;
  const scope = String(o.scope ?? "all").trim().toLowerCase();
  if (scope === "selected") {
    const organizationIds = Array.isArray(o.organizationIds)
      ? o.organizationIds.map((id) => String(id)).filter(Boolean)
      : [];
    return { scope: "selected", organizationIds };
  }
  return { scope: "all" };
}

function buildOrganizationAssignmentPayload(
  scope: OrganizationAssignmentScope,
  organizationIds: string[],
): OrganizationAssignment {
  if (scope === "all") return { scope: "all" };
  return {
    scope: "selected",
    organizationIds: [...new Set(organizationIds.filter(Boolean))],
  };
}

function mapApiUser(raw: unknown): ManagedUser {
  const o =
    typeof raw === "object" && raw !== null ? (raw as Record<string, unknown>) : {};
  const id = o._id ?? o.id;
  const perms =
    typeof o.permissions === "object" && o.permissions !== null
      ? (o.permissions as Record<string, boolean>)
      : {};
  const statusRaw = o.status ?? o.accessStatus;
  const status =
    statusRaw != null && statusRaw !== ""
      ? normalizeAccountStatus(statusRaw)
      : o.enabled === false || o.isActive === false
        ? "suspended"
        : "active";
  const enabled = status === "active";
  return {
    id: id != null ? String(id) : crypto.randomUUID(),
    name: String(o.name ?? o.firstName ?? "").trim(),
    email: String(o.email ?? "").trim(),
    position: String(o.position ?? "manager").trim() || "manager",
    status,
    enabled,
    permissions: mergeStoredPermissions(perms),
    organizationAssignment: parseOrganizationAssignment(o.organizationAssignment),
  };
}

function buildUserUpdatePayload(
  user: Pick<
    ManagedUser,
    "name" | "email" | "position" | "status" | "organizationAssignment"
  >,
) {
  const payload: Record<string, unknown> = {
    name: user.name,
    email: user.email,
    position: user.position,
    status: user.status,
  };
  if (!isAdminPosition(user.position)) {
    payload.organizationAssignment = user.organizationAssignment;
  }
  return capitalizeTheStartingCharacterFilter(payload, ["name"]);
}

function buildUserCreatePayload(
  user: Pick<ManagedUser, "name" | "email" | "position" | "organizationAssignment">,
) {
  const payload: Record<string, unknown> = {
    name: user.name,
    email: user.email,
    position: user.position,
  };
  if (!isAdminPosition(user.position)) {
    payload.organizationAssignment = user.organizationAssignment;
  }
  return capitalizeTheStartingCharacterFilter(payload, ["name"]);
}

function extractOrganizationsList(body: unknown): OrgListItem[] {
  if (!body || typeof body !== "object") return [];
  const root = body as Record<string, unknown>;
  const data = root.data ?? root;
  let list: unknown[] = [];
  if (Array.isArray(data)) list = data;
  else if (typeof data === "object" && data !== null) {
    const block = data as Record<string, unknown>;
    if (Array.isArray(block.organizations)) list = block.organizations;
  }
  return list
    .map((row) => {
      const o =
        typeof row === "object" && row !== null
          ? (row as Record<string, unknown>)
          : {};
      const id = o._id ?? o.id;
      if (id == null) return null;
      return {
        _id: String(id),
        organizationName: String(o.organizationName ?? o.name ?? id).trim(),
      };
    })
    .filter((x): x is OrgListItem => x != null);
}

function toTableRow(u: ManagedUser) {
  return {
    _id: u.id,
    firstName: u.name,
    email: u.email,
    position: u.position,
    enabled: u.enabled !== false,
    status: u.status,
    __user: u,
  };
}

export default function UserManagement() {
  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [listVersion, setListVersion] = useState(0);
  const [filters, setFilters] = useState<Record<string, unknown>>({});

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [position, setPosition] = useState<string>("manager");
  const [accountStatus, setAccountStatus] = useState<UserAccountStatus>("active");
  const [orgScope, setOrgScope] = useState<OrganizationAssignmentScope>("all");
  const [selectedOrganizationIds, setSelectedOrganizationIds] = useState<string[]>(
    [],
  );
  const [organizations, setOrganizations] = useState<OrgListItem[]>([]);
  const [orgsLoading, setOrgsLoading] = useState(false);

  const [permDialogOpen, setPermDialogOpen] = useState(false);
  const [permTarget, setPermTarget] = useState<ManagedUser | null>(null);
  const [permSaving, setPermSaving] = useState(false);

  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState<ManagedUser | null>(null);
  const [passwordSaving, setPasswordSaving] = useState(false);

  const fallbackNavKeys = FALLBACK_NAV_KEYS;
  const [permOnlyDraft, setPermOnlyDraft] = useState<Record<string, boolean>>(() =>
    emptyAccessPermissionDraft(fallbackNavKeys),
  );
  /** Avoid reusing another user's toggles when opening the permissions dialog */
  const permDraftSyncedForUserIdRef = useRef<string | null>(null);
  const permTargetRef = useRef<ManagedUser | null>(null);
  permTargetRef.current = permTarget;

  const fallbackNavItems: PermissionCatalogItem[] = useMemo(
    () =>
      ACCESS_NAV_PERMISSION_ITEMS.map(({ key, label }) => ({ key, label })),
    [],
  );

  /** Same list as modal fields / PUT payload keys — sourced from ACCESS_NAV_PERMISSION_ITEMS (matches list API `permissions` keys). */
  const catalogKeysList = useMemo(
    () => fallbackNavItems.map((i) => i.key),
    [fallbackNavItems],
  );

  useEffect(() => {
    if (!permDialogOpen) {
      permDraftSyncedForUserIdRef.current = null;
      return;
    }
    const user = permTargetRef.current;
    if (!user) {
      permDraftSyncedForUserIdRef.current = null;
      return;
    }
    const uid = user.id;
    setPermOnlyDraft((prev) => {
      if (permDraftSyncedForUserIdRef.current !== uid) {
        permDraftSyncedForUserIdRef.current = uid;
        return buildAccessDraftFromUser(user, catalogKeysList);
      }
      return mergeCatalogKeysIntoDraft(prev, user, catalogKeysList);
    });
  }, [permDialogOpen, permTarget?.id, catalogKeysList]);

  const isEdit = editingId !== null;

  useEffect(() => {
    if (!dialogOpen || !isManagerPosition(position)) return;
    let cancelled = false;
    (async () => {
      setOrgsLoading(true);
      try {
        const { data } = await AxiosInstance().get<unknown>("/organizations", {
          params: { limit: 100 },
        });
        if (!cancelled) setOrganizations(extractOrganizationsList(data));
      } catch {
        if (!cancelled) {
          setOrganizations([]);
          toast.error("Could not load organizations.");
        }
      } finally {
        if (!cancelled) setOrgsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, position]);

  useEffect(() => {
    if (!dialogOpen) return;
    if (isAdminPosition(position)) {
      setOrgScope("all");
      setSelectedOrganizationIds([]);
    }
  }, [dialogOpen, position]);

  const handleUsersFromApi = useCallback((raw: unknown) => {
    const list = Array.isArray(raw) ? raw : extractUsersList({ data: raw });
    setUsers(list.map(mapApiUser));
  }, []);

  const tableRows = useMemo(() => users.map((u) => toTableRow(u)), [users]);

  const resetForm = () => {
    setEditingId(null);
    setName("");
    setEmail("");
    setPosition("manager");
    setAccountStatus("active");
    setOrgScope("all");
    setSelectedOrganizationIds([]);
  };

  const applyOrganizationAssignment = (assignment: OrganizationAssignment) => {
    if (assignment.scope === "selected") {
      setOrgScope("selected");
      setSelectedOrganizationIds([...assignment.organizationIds]);
    } else {
      setOrgScope("all");
      setSelectedOrganizationIds([]);
    }
  };

  const toggleOrganizationId = (orgId: string, checked: boolean) => {
    setSelectedOrganizationIds((prev) => {
      if (checked) return prev.includes(orgId) ? prev : [...prev, orgId];
      return prev.filter((id) => id !== orgId);
    });
  };

  const openAdd = () => {
    resetForm();
    setDialogOpen(true);
  };

  const handlePositionChange = (value: string) => {
    setPosition(value);
    if (isAdminPosition(value)) {
      setOrgScope("all");
      setSelectedOrganizationIds([]);
    }
  };

  const openEdit = (u: ManagedUser) => {
    setEditingId(u.id);
    setName(u.name);
    setEmail(u.email);
    setPosition(u.position);
    setAccountStatus(u.status);
    if (isAdminPosition(u.position)) {
      setOrgScope("all");
      setSelectedOrganizationIds([]);
    } else {
      applyOrganizationAssignment(u.organizationAssignment);
    }
    setDialogOpen(true);
  };

  const openPermissionsOnly = (u: ManagedUser) => {
    setPermTarget(u);
    setPermDialogOpen(true);
  };

  const openChangePassword = (u: ManagedUser) => {
    setPasswordTarget(u);
    setPasswordDialogOpen(true);
  };

  const handleAdminResetPassword = async (formData: { password: string }) => {
    if (!passwordTarget) return;
    const { pr, resolveFn, rejectFn } = getPromise();
    getPromiseToast(pr);
    try {
      setPasswordSaving(true);
      await AxiosInstance().post("/auth/admin/reset-admin-manager-password", {
        userId: passwordTarget.id,
        password: formData.password,
      });
      resolveFn("Password updated.");
      setPasswordDialogOpen(false);
      setPasswordTarget(null);
    } catch (error) {
      rejectFn(error);
    } finally {
      setPasswordSaving(false);
    }
  };

  const rowToUser = (row: any): ManagedUser | undefined => row?.__user;

  const toggleAccessPermission = useCallback((key: string, checked: boolean) => {
    setPermOnlyDraft((p) => ({ ...p, [key]: checked }));
  }, []);

  const handleDialogOpenChange = (open: boolean) => {
    setDialogOpen(open);
    if (!open) resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmedName = name.trim();
    const trimmedEmail = email.trim().toLowerCase();
    if (!trimmedName) {
      toast.error("Please enter a name.");
      return;
    }
    if (!trimmedEmail || !EMAIL_RE.test(trimmedEmail)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    const emailTaken = users.some(
      (u) => u.email.toLowerCase() === trimmedEmail && u.id !== editingId,
    );
    if (emailTaken) {
      toast.error("Another user already uses this email.");
      return;
    }
    if (
      isManagerPosition(position) &&
      orgScope === "selected" &&
      selectedOrganizationIds.length === 0
    ) {
      toast.error("Select at least one organization.");
      return;
    }

    const organizationAssignment = isAdminPosition(position)
      ? ALL_ORGANIZATIONS_ASSIGNMENT
      : buildOrganizationAssignmentPayload(orgScope, selectedOrganizationIds);

    const { pr, resolveFn, rejectFn } = getPromise();
    getPromiseToast(pr);
    try {
      setSubmitting(true);
      if (isEdit && editingId) {
        await AxiosInstance().put(
          `/users/${editingId}`,
          buildUserUpdatePayload({
            name: trimmedName,
            email: trimmedEmail,
            position,
            status: accountStatus,
            organizationAssignment,
          }),
        );
        setUsers((prev) =>
          prev.map((u) =>
            u.id === editingId
              ? {
                  ...u,
                  name: trimmedName,
                  email: trimmedEmail,
                  position,
                  status: accountStatus,
                  enabled: accountStatus === "active",
                  organizationAssignment,
                }
              : u,
          ),
        );
        resolveFn("User updated.");
      } else {
        await AxiosInstance().post(
          "/users",
          buildUserCreatePayload({
            name: trimmedName,
            email: trimmedEmail,
            position,
            organizationAssignment,
          }),
        );
        resolveFn("User added. Set permissions from Actions → Change permissions.");
      }
      setListVersion((v) => v + 1);
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      rejectFn(error);
    } finally {
      setSubmitting(false);
    }
  };

  const savePermissionsOnly = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!permTarget) return;
    const { permissions } = buildAccessPermissionsPayload(
      permOnlyDraft,
      catalogKeysList,
    );
    const { pr, resolveFn, rejectFn } = getPromise();
    getPromiseToast(pr);
    try {
      setPermSaving(true);
      await AxiosInstance().put(
        `/users/${permTarget.id}/permissions`,
        { permissions },
      );
      setUsers((prev) =>
        prev.map((u) =>
          u.id === permTarget.id
            ? { ...u, permissions: { ...u.permissions, ...permissions } }
            : u,
        ),
      );
      resolveFn("Permissions updated.");
      setPermDialogOpen(false);
      setPermTarget(null);
    } catch (error) {
      rejectFn(error);
    } finally {
      setPermSaving(false);
    }
  };

  const setFiltersSafe = useCallback((next: any) => {
    if (typeof next === "function") {
      setFilters((prev) => next(prev));
    } else {
      setFilters(next ?? {});
    }
  }, []);

  const userMoreActions = useMemo(
    () => [
      {
        label: "Change permissions",
        icon: <KeyRound className="h-4 w-4" />,
        onClick: (row: any) => {
          const u = rowToUser(row);
          if (u) openPermissionsOnly(u);
        },
        isShow: (row: any) => {
          const u = rowToUser(row);
          return !!u && isManagerPosition(u.position);
        },
      },
      {
        label: "Change password",
        icon: <Lock className="h-4 w-4" />,
        onClick: (row: any) => {
          const u = rowToUser(row);
          if (u) openChangePassword(u);
        },
        isShow: () => true,
      },
    ],
    [],
  );

  return (
    <DashboardLayout
      activePage="/user-management"
      rightcomponent={
        <div className="flex gap-2 w-full sm:w-auto">
          <Button className="flex-1 sm:flex-initial" type="button" onClick={openAdd}>
            <Plus className="h-4 w-4" />
            User
          </Button>
        </div>
      }
    >
      <div className="">
        <TableWithSearch
          columnPreferenceKey="user-management-list"
          preferenceTableName="user-management"
          apiDataKey="users"
          url="/users"
          universalLoadingSkeleton
          fetchCondition
          isIgnoreSearchParams
          data={tableRows}
          setData={handleUsersFromApi}
          viewData={tableRows}
          columnHeaders={userManagementTableHeaders}
          heading="Users List"
          isIncludeSearch
          searchPlaceholder="Search users ..."
          isActionColumn
          handleAction={(row: any) => {
            const u = rowToUser(row);
            if (u) openEdit(u);
          }}
          moreActions={userMoreActions}
          isActionsHidden={(row: any) => {
            const email = String(
              row?.email ?? rowToUser(row)?.email ?? "",
            )
              .trim()
              .toLowerCase();
            return email === PROTECTED_ADMIN_EMAIL;
          }}
          clickRow={() => {}}
          filtersOptions={[]}
          filters={filters}
          setFilters={setFiltersSafe}
          defaultFilters={{}}
          isColumnSelection
          params={{ listVersion }}
        />
      </div>

      <Dialog open={dialogOpen} onOpenChange={handleDialogOpenChange}>
        <DialogContent className="flex max-h-[min(85dvh,720px)] flex-col overflow-hidden sm:max-w-md sm:gap-0">
          <form
            onSubmit={handleSubmit}
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            <DialogHeader className="shrink-0 space-y-2 text-left">
              <DialogTitle>{isEdit ? "Edit user" : "Add user"}</DialogTitle>
              <DialogDescription className="sr-only">
                {isEdit ? "Update user details" : "Create a new user"}
              </DialogDescription>
            </DialogHeader>
            <div className="min-h-0 flex-1 space-y-4 overflow-y-auto overscroll-contain py-1 px-1">
              <div className="space-y-2">
                <Label htmlFor="um-name">Full name</Label>
                <Input
                  id="um-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                  placeholder="e.g. Jordan Lee"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="um-email">Work email</Label>
                <Input
                  id="um-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="name@organization.com"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="um-position">Position</Label>
                <Select value={position} onValueChange={handlePositionChange}>
                  <SelectTrigger id="um-position" className="w-full">
                    <SelectValue placeholder="Select position" />
                  </SelectTrigger>
                  <SelectContent className="z-[300]">
                    {POSITION_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {isEdit && (
                accountStatus !== "inactive" ? (
                <div className="space-y-2">
                  <Label htmlFor="um-status">Status</Label>
                  <Select
                    value={accountStatus}
                    onValueChange={(v) =>
                      setAccountStatus(v as UserAccountStatus)
                    }
                  >
                    <SelectTrigger id="um-status" className="w-full">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent className="z-[300]">
                      {USER_ACCOUNT_STATUS_OPTIONS.map((o) => (
                        <SelectItem key={o.value} value={o.value}>
                          {o.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label htmlFor="um-status">Status</Label>
                  <Input id="um-status" value="Inactive" disabled />
                  {/* <p className="text-sm text-muted-foreground">
                  This account is inactive until the user completes password setup.
                  </p>        */}
                           </div>
              ))}
              {isAdminPosition(position) ? (
                <p className="text-sm text-muted-foreground rounded-md border border-border bg-muted/25 px-3 py-2">
                  Admins always have access to all organizations.
                </p>
              ) : isManagerPosition(position) ? (
                <div className="space-y-3">
                  <Label>Organization assignment</Label>
                  <RadioGroup
                    value={orgScope}
                    onValueChange={(v) =>
                      setOrgScope(v as OrganizationAssignmentScope)
                    }
                    className="gap-2"
                  >
                    <div className="flex items-center gap-2">
                      <RadioGroupItem value="all" id="um-org-scope-all" />
                      <label
                        htmlFor="um-org-scope-all"
                        className="text-sm cursor-pointer"
                      >
                        All organizations
                      </label>
                    </div>
                    <div className="flex items-center gap-2">
                      <RadioGroupItem
                        value="selected"
                        id="um-org-scope-selected"
                      />
                      <label
                        htmlFor="um-org-scope-selected"
                        className="text-sm cursor-pointer"
                      >
                        Selected organizations
                      </label>
                    </div>
                  </RadioGroup>
                  {orgScope === "selected" && (
                    <div className="rounded-lg border border-border bg-muted/25 p-2 max-h-40 overflow-y-auto space-y-1">
                      {orgsLoading ? (
                        <p className="text-sm text-muted-foreground px-2 py-3">
                          Loading organizations…
                        </p>
                      ) : organizations.length === 0 ? (
                        <p className="text-sm text-muted-foreground px-2 py-3">
                          No organizations available.
                        </p>
                      ) : (
                        organizations.map((org) => (
                          <div
                            key={org._id}
                            className="flex items-start gap-3 rounded-md px-2 py-2 hover:bg-muted/70"
                          >
                            <Checkbox
                              id={`um-org-${org._id}`}
                              checked={selectedOrganizationIds.includes(org._id)}
                              onCheckedChange={(v) =>
                                toggleOrganizationId(org._id, v === true)
                              }
                              className="mt-0.5"
                            />
                            <label
                              htmlFor={`um-org-${org._id}`}
                              className="text-sm leading-snug cursor-pointer"
                            >
                              {org.organizationName}
                            </label>
                          </div>
                        ))
                      )}
                    </div>
                  )}
                  <p className="text-xs text-muted-foreground">
                    {orgScope === "all"
                      ? "User can access all organizations."
                      : "User can only access the organizations you select."}
                  </p>
                </div>
              ) : null}
            </div>
            <DialogFooter className="mt-0 shrink-0 gap-2 border-t bg-background pt-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleDialogOpenChange(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={submitting}>
                {submitting
                  ? isEdit
                    ? "Saving…"
                    : "Adding…"
                  : isEdit
                    ? "Save changes"
                    : "Add user"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={permDialogOpen}
        onOpenChange={(open) => {
          setPermDialogOpen(open);
          if (!open) {
            setPermTarget(null);
            permDraftSyncedForUserIdRef.current = null;
          }
        }}
      >
        <DialogContent className="max-h-[85dvh] overflow-y-auto sm:max-w-md sm:gap-4">
          <form onSubmit={savePermissionsOnly}>
            <DialogHeader className="space-y-2 text-left">
              <DialogTitle>Change permissions</DialogTitle>
              <DialogDescription className="text-muted-foreground text-sm">
                Control which areas of the app this user can access (navigation).
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="flex items-baseline justify-between gap-2">
                <Label>Access</Label>
                <span className="text-xs text-muted-foreground tabular-nums">
                  {catalogKeysList.filter((k) => permOnlyDraft[k]).length}
                  {" of "}
                  {catalogKeysList.length}
                </span>
              </div>
              <AccessNavPermissionFields
                items={fallbackNavItems}
                draft={permOnlyDraft}
                onToggle={toggleAccessPermission}
                idPrefix="perm-only"
              />
            </div>
            <DialogFooter className="gap-2 border-t pt-4 sm:justify-end">
              <Button
                type="button"
                variant="outline"
                disabled={permSaving}
                onClick={() => {
                  setPermDialogOpen(false);
                  setPermTarget(null);
                }}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={permSaving}>
                {permSaving ? "Saving…" : "Save permissions"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={passwordDialogOpen}
        onOpenChange={(open) => {
          setPasswordDialogOpen(open);
          if (!open) setPasswordTarget(null);
        }}
      >
        <DialogContent className="max-h-[85dvh] overflow-y-auto sm:max-w-md sm:gap-4">
          <DialogHeader className="space-y-2 text-left">
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription className="text-muted-foreground text-sm">
              {passwordTarget
                ? `Set a new password for ${passwordTarget.name} (${passwordTarget.email}).`
                : "Select a user from the table."}
            </DialogDescription>
          </DialogHeader>
          {passwordTarget ? (
            <RHFWrapper
              key={passwordTarget.id}
              initialValues={{ password: "", confirmPassword: "" }}
              isValidate
              validateSchema={ResetSchema}
              className="space-y-4"
              onSubmit={(data) => void handleAdminResetPassword(data)}
            >
              <RhfInput
                name="password"
                label="New password"
                type="password"
                placeholder="New password"
                autoComplete="new-password"
              />
              <RhfInput
                name="confirmPassword"
                label="Confirm password"
                type="password"
                placeholder="Confirm password"
                autoComplete="new-password"
              />
              <DialogFooter className="gap-2 border-t pt-4 sm:justify-end sm:gap-2">
                <Button
                  type="button"
                  variant="outline"
                  disabled={passwordSaving}
                  onClick={() => {
                    setPasswordDialogOpen(false);
                    setPasswordTarget(null);
                  }}
                >
                  Cancel
                </Button>
                <Button type="submit" disabled={passwordSaving}>
                  {passwordSaving ? "Saving…" : "Update password"}
                </Button>
              </DialogFooter>
            </RHFWrapper>
          ) : null}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
